// web/src/features/home/HomePage.tsx
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import '@/features/home/widgets'; // side-effect: registers widgets
import { api } from '@/shared/api/client';
import { useAuth } from '@/shared/hooks/useAuth';
import { useHomeLayout } from './useHomeLayout';
import { removeWidget } from './groups';
import { resolveContainerBoxes, applyBoxes, ensurePositions } from './placeContainer';
import { moveElement, resolveResize, resolveResizeBox } from './place';
import { HomeCanvas } from './HomeCanvas';
import { HomeCustomize } from './HomeCustomize';
import { PageActions } from '@/components/ui/PageActions';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { DropdownMenu } from '@/components/ui/DropdownMenu';
import { widgetRegistry, getWidgetDef } from './registry';
import { widgetWH, GRID_COLS } from './resize';
import { prefsWithTempUnit, resolveTempUnit, useTempUnitDraft } from './tempUnit';
import { TempUnitToggle } from './TempUnitToggle';
import type { HomeLayout, WidgetType } from './types';

type Role = { id: string; code: string; name: string };
type EditScope = { kind: 'user' } | { kind: 'role'; roleKey: string; label: string };

let seq = 0;
function newId() { seq += 1; return `w-${seq}-${(globalThis.performance?.now?.() ?? seq).toString(36)}`; }

export function HomePage() {
  const { user } = useAuth();
  const { layout, isLoading, save, reset, saveRole, loadRole } = useHomeLayout();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<HomeLayout>({ version: 1, widgets: [] });
  const [editScope, setEditScope] = useState<EditScope>({ kind: 'user' });
  const isAdmin = (user?.roles ?? []).some((r) => r === 'admin' || r === 'superadmin');
  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: () => api.get<Role[]>('/roles'), enabled: isAdmin });
  // The temperature unit being picked (°F/°C in the bar): set while customizing
  // My Home, saved with the layout, dropped on Cancel or when Home closes.
  const setUnitDraft = useTempUnitDraft((s) => s.setUnit);
  useEffect(() => () => setUnitDraft(null), [setUnitDraft]);

  // Hooks must run before any early return.
  if (isLoading) return <div className="p-4 text-sm text-muted">Loading...</div>;

  const startEdit = () => {
    setDraft({ version: 1, widgets: layout.widgets }); setEditScope({ kind: 'user' }); setEditing(true);
    setUnitDraft(resolveTempUnit(layout));
  };
  const add = (type: WidgetType) => {
    const def = getWidgetDef(type); if (!def) return;
    const wh = def.defaultWH ?? widgetWH({ size: def.defaultSize });
    setDraft((d) => ({ ...d, widgets: [...d.widgets, { id: newId(), type, w: wh.w, h: wh.h, config: {} }] }));
  };
  const remove = (id: string) => setDraft((d) => removeWidget(d, id));
  const setConfig = (id: string, config: Record<string, unknown>) =>
    setDraft((d) => ({ ...d, widgets: d.widgets.map((x) => x.id === id ? { ...x, config } : x) }));

  // Free placement over the single grid: run the push-aside engine on all widgets,
  // write the resulting x/y (and w/h on resize) back onto the draft by id.
  const onMove = (id: string, x: number, y: number) =>
    setDraft((d) => ({ ...d, widgets: applyBoxes(d.widgets, moveElement(resolveContainerBoxes(d.widgets), id, x, y, GRID_COLS)) }));
  const applyResize = (d: HomeLayout, id: string, wcols: number, hrows: number): HomeLayout => {
    const resized = resolveResize(resolveContainerBoxes(d.widgets), id, wcols, hrows, GRID_COLS);
    const rb = resized.find((box) => box.id === id);
    let widgets = applyBoxes(d.widgets, resized);
    if (rb) widgets = widgets.map((w) => (w.id === id ? { ...w, w: rb.w, h: rb.h, size: undefined } : w));
    return { ...d, widgets };
  };
  // Corner resize: the box can also move (x/y), so persist the full geometry.
  const resizeBox = (id: string, x: number, y: number, wcols: number, hrows: number) =>
    setDraft((d) => {
      const resized = resolveResizeBox(resolveContainerBoxes(d.widgets), id, { x, y, w: wcols, h: hrows }, GRID_COLS);
      const rb = resized.find((box) => box.id === id);
      let widgets = applyBoxes(d.widgets, resized);
      if (rb) widgets = widgets.map((w) => (w.id === id ? { ...w, w: rb.w, h: rb.h, size: undefined } : w));
      return { ...d, widgets };
    });
  const setSize = (id: string, wcols: number) =>
    setDraft((d) => {
      const cur = resolveContainerBoxes(d.widgets).find((box) => box.id === id);
      return applyResize(d, id, wcols, cur?.h ?? 3);
    });

  // Switch which layout the editor is editing. A role's layout carries no
  // personal preference, so the unit toggle is offered for My Home only.
  const pickScope = async (value: string) => {
    if (!value) {
      setDraft({ version: 1, widgets: layout.widgets }); setEditScope({ kind: 'user' });
      setUnitDraft(resolveTempUnit(layout));
      return;
    }
    const role = (rolesQ.data ?? []).find((r) => r.code === value);
    if (!role) return;
    const resp = await loadRole(role.code);
    setDraft({ version: 1, widgets: resp.layout.widgets ?? [] });
    setEditScope({ kind: 'role', roleKey: role.code, label: role.name });
    setUnitDraft(null);
  };

  const exitEdit = () => { setEditing(false); setEditScope({ kind: 'user' }); setUnitDraft(null); };
  const handleSave = async () => {
    // Flatten (grouping removed) + persist explicit x/y for every widget.
    const finalDraft = ensurePositions({ version: draft.version, widgets: draft.widgets });
    if (editScope.kind === 'role') await saveRole(editScope.roleKey, finalDraft);
    else {
      // My Home also keeps the user's preferences (the unit picked in the bar).
      const prefs = prefsWithTempUnit(layout, useTempUnitDraft.getState().unit);
      await save(prefs ? { ...finalDraft, prefs } : finalDraft);
    }
    exitEdit();
  };
  const handleReset = async () => { await reset(); exitEdit(); };

  const addItems = Object.values(widgetRegistry)
    .filter((d): d is NonNullable<typeof d> => Boolean(d))
    .filter((def) => !def.hideFromPicker)
    .map((def) => ({
      label: def.label,
      // Same accent chip the widget wears on its card header, so the picker
      // reads as "these are the widgets" rather than a bare text list.
      icon: (
        <span
          className="w-4 h-4 rounded-md inline-flex items-center justify-center text-white [&_svg]:w-2.5 [&_svg]:h-2.5"
          style={{ background: def.accent }}
        >
          {def.icon}
        </span>
      ),
      onClick: () => add(def.type),
    }));

  return (
    <div>
      <PageActions>
        {editing ? (
          <>
            {isAdmin && (
              <div className="w-44">
                <Select
                  value={editScope.kind === 'role' ? editScope.roleKey : ''}
                  onChange={(e) => void pickScope(e.target.value)}
                  title="Choose whose Home layout to edit"
                >
                  <option value="">My Home</option>
                  {(rolesQ.data ?? []).map((r) => <option key={r.id} value={r.code}>Role: {r.name}</option>)}
                </Select>
              </div>
            )}
            <TempUnitToggle />
            <DropdownMenu
              align="right"
              items={addItems}
              trigger={
                <div className="inline-flex items-center gap-1.5 h-7 px-3 rounded-[8px] bg-surface text-primary-text text-[0.75rem] font-medium shadow-marked hover:bg-canvas-soft transition cursor-pointer select-none">
                  Add widget
                </div>
              }
            />
            {editScope.kind === 'user' && <Button size="sm" variant="secondary" onClick={handleReset}>Reset to default</Button>}
            <Button size="sm" variant="secondary" onClick={exitEdit}>Cancel</Button>
            <Button size="sm" variant="primary" onClick={handleSave}>
              {editScope.kind === 'role' ? `Save (${editScope.label})` : 'Save'}
            </Button>
          </>
        ) : (
          <Button size="sm" variant="secondary" onClick={startEdit}>Customize</Button>
        )}
      </PageActions>

      {editing
        ? <HomeCustomize
            layout={draft}
            onMove={onMove}
            onResizeBox={resizeBox}
            onSetSize={setSize}
            onRemove={remove}
            onSetConfig={setConfig}
          />
        : <HomeCanvas layout={layout} />}
    </div>
  );
}
